/**
 * Shared helpers for the CheckoutModal component family (3 variants:
 * physical / service / digital).
 *
 * SPEC-244 · partición VERBATIM de
 * frontend/src/components/market/checkout-modal.tsx (2.637 L).
 *
 * All helpers preserved VERBATIM from the original monolith:
 *   - `resolveShipping(product)` — effective shipping config (INV-MKT-21).
 *   - `resolvePickupInfo(product)` — effective pickup address (Founder
 *     directive 2026-10-05).
 *   - `estimateOrder(...)` — UX-affordance total estimate (display only;
 *     the backend's `grandTotalCredits` is authoritative per INV-SEC-02).
 *   - `formatCredits(value)` — crédit amount formatter (no symbol, up to
 *     2 decimals).
 *   - `mapCreateError(err)` — backend error code → Spanish user message.
 */

import { ApiClientError } from '@/lib/api-client';
import type {
  PublicProductResponse,
  FulfillmentMethod,
  VariationSelection,
} from '@profileia/shared';

// ─── resolveShipping ─────────────────────────────────────────────────────────

/**
 * Resolve the effective shipping config for the order estimate
 * (INV-MKT-21 + founder directive 2026-10-05 — per-product FULL override).
 *
 * Mirrors the `resolveShipping` helper on the product detail page:
 *   - `inheritSeller=true` → the store's shipping config (default).
 *   - `inheritSeller=false` → the product's `config.shipping` FIRST (its
 *     OWN full config — valid even when the store doesn't ship), else the
 *     legacy `shippingOverride` (methods from the store), else the store
 *     default.
 *
 * Returns `null` when shipping isn't offered for this product (the
 * backend would reject a ship order with 400 PRODUCT_NOT_SHIPPABLE —
 * defense in depth).
 */
export function resolveShipping(product: PublicProductResponse): {
  cost: string;
  freeShippingThreshold: string | null;
  minOrderCredits: string;
} | null {
  const seller = product.seller.fulfillmentOptions;
  const f = product.fulfillment;
  const offersShips = (f.modalities ?? []).includes('ships');

  if (f.inheritSeller) {
    if (!seller.ships) return null;
    return seller.shipping;
  }

  // Founder directive 2026-10-05 — per-product FULL shipping config.
  if (f.config?.shipping) {
    return {
      cost: f.config.shipping.cost,
      freeShippingThreshold: f.config.shipping.freeShippingThreshold ?? null,
      minOrderCredits: f.config.shipping.minOrderCredits ?? '0',
    };
  }

  // Legacy override (cost/thresholds from the product; the store must
  // ship — the methods come from the store).
  const override = f.shippingOverride;
  if (override && (offersShips || (f.modalities ?? []).length === 0)) {
    if (!seller.ships || !seller.shipping) return null;
    return {
      cost: override.cost,
      freeShippingThreshold: override.freeShippingThreshold,
      minOrderCredits: override.minOrderCredits,
    };
  }

  if (!offersShips) return null;
  return seller.ships ? seller.shipping : null;
}

// ─── resolvePickupInfo ───────────────────────────────────────────────────────

/**
 * Founder directive 2026-10-05 — resolve the effective PICKUP address for
 * the checkout flow: the product's `config.pickup` locations first (its
 * OWN addresses), else the store's pickupConfig (default). Returns the
 * first location (the checkout confirms ONE pickup address) + metadata.
 */
export function resolvePickupInfo(product: PublicProductResponse): {
  address: string;
  lat: number;
  lng: number;
  label: string | null;
  instructions: string;
  hours: string;
} | null {
  const seller = product.seller.fulfillmentOptions;
  const f = product.fulfillment;
  if (f.inheritSeller) {
    if (!seller.pickup || !seller.pickupConfig) return null;
    return {
      address: seller.pickupConfig.location.address,
      lat: seller.pickupConfig.location.lat,
      lng: seller.pickupConfig.location.lng,
      label: null,
      instructions: seller.pickupConfig.instructions,
      hours: seller.pickupConfig.hours,
    };
  }
  if (!(f.modalities ?? []).includes('pickup')) return null;
  const customPickup = f.config?.pickup;
  const customLoc = customPickup?.locations?.[0];
  if (customLoc) {
    return {
      address: customLoc.address,
      lat: customLoc.lat,
      lng: customLoc.lng,
      label: customLoc.label ?? null,
      instructions: customPickup?.instructions ?? '',
      hours: customPickup?.hours ?? '',
    };
  }
  if (seller.pickup && seller.pickupConfig) {
    return {
      address: seller.pickupConfig.location.address,
      lat: seller.pickupConfig.location.lat,
      lng: seller.pickupConfig.location.lng,
      label: null,
      instructions: seller.pickupConfig.instructions,
      hours: seller.pickupConfig.hours,
    };
  }
  return null;
}

// ─── estimateOrder ───────────────────────────────────────────────────────────

/**
 * Estimate the order total for display in Step 2. The backend's
 * `grandTotalCredits` (computed with BigInt-based `Decimal` per INV-SEC-02)
 * is authoritative — this is a UX affordance.
 *
 * Returns the unit price (créditos), total price (unit × qty), shipping
 * cost (0 if pickup or free-shipping threshold met), and grand total.
 * All as numbers (parseFloat — display only).
 */
export function estimateOrder(
  product: PublicProductResponse,
  variationSelection: VariationSelection | null,
  quantity: number,
  fulfillmentMethod: FulfillmentMethod | null,
): {
  unitPrice: number;
  totalPrice: number;
  shippingCost: number;
  grandTotal: number;
  freeShippingApplied: boolean;
} {
  const basePrice = parseFloat(product.priceCredits);
  const variationDelta = variationSelection
    ? (() => {
        const variation = product.variations[0];
        if (!variation) return 0;
        const opt = variation.options.find((o) => o.name === variationSelection.option);
        return opt ? parseFloat(opt.priceDelta) : 0;
      })()
    : 0;
  const unitPrice = basePrice + variationDelta;
  const totalPrice = unitPrice * quantity;

  if (fulfillmentMethod !== 'ship') {
    return {
      unitPrice,
      totalPrice,
      shippingCost: 0,
      grandTotal: totalPrice,
      freeShippingApplied: false,
    };
  }

  const shipping = resolveShipping(product);
  if (!shipping) {
    return {
      unitPrice,
      totalPrice,
      shippingCost: 0,
      grandTotal: totalPrice,
      freeShippingApplied: false,
    };
  }

  const shippingCostBase = parseFloat(shipping.cost);
  const threshold = shipping.freeShippingThreshold
    ? parseFloat(shipping.freeShippingThreshold)
    : null;
  // Free shipping applies when the threshold is set AND the order's total
  // (before shipping) meets or exceeds it. Mirrors the backend's
  // `escrow.service.createOrder` step 4 logic.
  const freeShippingApplied =
    threshold !== null && !Number.isNaN(threshold) && totalPrice >= threshold;
  const shippingCost = freeShippingApplied ? 0 : shippingCostBase;
  return {
    unitPrice,
    totalPrice,
    shippingCost,
    grandTotal: totalPrice + shippingCost,
    freeShippingApplied,
  };
}

// ─── formatCredits ───────────────────────────────────────────────────────────

/**
 * Format a number as a crédit amount string (no currency symbol, just the
 * number with up to 2 decimals). Used for display in summary steps.
 */
export function formatCredits(value: number): string {
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(2).replace(/\.?0+$/, '');
}

// ─── mapCreateError ──────────────────────────────────────────────────────────

/**
 * Map a backend error code from `api.market.orders.create` to a user-
 * facing Spanish message. Shared by all 3 variants (physical / service /
 * digital) — the 30+ known codes come from `escrow.service.ts` (Task B +
 * Task B1) + the controller layer.
 *
 * Returns `null` for unknown codes — the caller falls back to `err.message`.
 *
 * §6 Anti-Invención: NO new error codes invented — the messages mirror
 * the existing Fase 4 patterns verbatim, with Fase 5 additions for
 * service + digital specific codes (SERVICE_*, DIGITAL_*) when they
 * surface from the backend (Task B1 + B2 added the throw sites).
 */
export function mapCreateError(err: ApiClientError): string | null {
  const code = err.code ?? '';
  if (code === 'INSUFFICIENT_CREDITS') {
    return 'No tienes créditos suficientes para esta compra. Carga más créditos en Profileia Market e intenta de nuevo.';
  }
  if (code === 'PRODUCT_NOT_APPROVED' || code === 'PRODUCT_NOT_FOUND') {
    return 'El producto ya no está disponible. Recarga la página para ver el catálogo actualizado.';
  }
  if (code === 'STOCK_UNAVAILABLE' || code === 'OUT_OF_STOCK') {
    return 'Lo sentimos, la cantidad solicitada ya no está en stock. Intenta con una cantidad menor.';
  }
  if (code === 'PRODUCT_NOT_SHIPPABLE') {
    return 'Este vendedor no ofrece envío. Selecciona "Recogida en local" o contacta al vendedor.';
  }
  if (code === 'PRODUCT_NOT_PICKUP') {
    return 'Este vendedor no ofrece recogida. Selecciona "Envío a domicilio".';
  }
  if (code === 'ORDER_BELOW_MINIMUM') {
    return 'El total de tu orden no alcanza el mínimo requerido por el vendedor para envío. Revisa el mínimo en la sección de fulfillment.';
  }
  if (code === 'RATE_LIMIT_EXCEEDED') {
    return 'Límite de órdenes alcanzado (10/hora). Intenta de nuevo más tarde.';
  }
  if (code === 'MARKET_ORDERS_DISABLED') {
    return 'Profileia Market no está habilitado en este momento. Intenta más tarde.';
  }
  if (code === 'VARIATION_REQUIRED' || code === 'VARIATION_INVALID') {
    return 'Selecciona una variación válida antes de continuar.';
  }
  if (code === 'IDEMPOTENCY_CONFLICT') {
    return 'Conflicto de idempotencia — intenta de nuevo con una nueva orden.';
  }
  if (code === 'SERVICE_RESERVATION_REQUIRED') {
    return 'Selecciona una fecha y rango horario válidos para tu reserva.';
  }
  if (code === 'SERVICE_RESERVATION_PAST') {
    return 'La fecha de reserva debe ser futura. Selecciona una fecha posterior a hoy.';
  }
  if (code === 'SERVICE_RESERVATION_INVALID_RANGE') {
    return 'La hora de fin debe ser posterior a la hora de inicio.';
  }
  if (code === 'SERVICE_GUEST_COUNT_INVALID') {
    return 'El número de invitados debe ser un entero mayor o igual a 1.';
  }
  if (code === 'PRODUCT_NOT_SERVICE') {
    return 'Este producto no es un servicio. Recarga la página o contacta al vendedor.';
  }
  if (code === 'PRODUCT_NOT_DIGITAL') {
    return 'Este producto no es digital. Recarga la página o contacta al vendedor.';
  }
  if (code === 'DIGITAL_DOWNLOAD_UNAVAILABLE') {
    return 'La descarga digital no está disponible en este momento. Contacta al vendedor.';
  }
  if (code === 'SERVICE_CONFIG_MISSING') {
    return 'La configuración del servicio está incompleta. Contacta al vendedor.';
  }
  if (code === 'DIGITAL_CONFIG_MISSING') {
    return 'La configuración del producto digital está incompleta. Contacta al vendedor.';
  }
  // SPEC-157 — single-purchase recurring: the buyer already bought this
  // product. We intercept this case in the create call's catch block
  // (toast.info "Ya eres cliente" + close modal) rather than showing an
  // inline error — the buyer is NOT in an error state, they are an existing
  // customer who needs to coordinate recurring payments off-platform.
  // Returning null here lets the catch block run its own UX path.
  if (code === 'SINGLE_PURCHASE_ALREADY_BOUGHT') {
    return null;
  }
  // Legacy Fase 4 code — still thrown by `createOrder` for non-physical
  // direct calls (the controller dispatches correctly, but the service
  // method itself enforces physical-only for backward compat with Fase 4
  // tests). Map to a friendly message just in case it surfaces.
  if (code === 'PRODUCT_NOT_PHYSICAL') {
    return 'Este producto no es físico — usa el flujo de checkout correspondiente (servicio o digital).';
  }
  return null;
}
